import classNames from 'classnames';
import * as React from 'react';
import { NavigationApplyClick } from '../Navigation/Events/NavigationApplyClick';
import type { CampusCardBlockViewModel } from '~/Blocks/CampusCardBlock/ViewModel/CampusCardBlockViewModel.csharp';
import type { StudyAdmissionCallToActionTranslations } from '~/Blocks/StudyAdmissionCallToActionBlock/ViewModel/StudyAdmissionCallToActionTranslations.csharp';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { MessageBroker } from '~/Features/Messaging/MessageBroker';
import type { ApplicationChoiceDto } from '~/Features/Search/ApplicationChoice/Controller/ApplicationChoiceDto.csharp';
import { AdmissionCallToActionOverlayStatusChanged } from '~/Features/StudyPage/Campus/Partials/AdmissionCallToAction/Events/AdmissionCallToActionOverlayStatusChanged';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { Link } from '~/Partials/Link/Link';
import { LinkType } from '~/Partials/Link/LinkType.csharp';
import type { LinkViewModel } from '~/Partials/Link/LinkViewModel.csharp';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { redirect } from '~/Partials/Utils/utils';
import type { Theme } from '~/Utils/Theme';
import styles from './AdmissionCallToAction.module.css';
import { AdmissionCallToActionCardsList } from './AdmissionCallToActionCardsList/AdmissionCallToActionCardsList';
import { AdmissionCallToActionOverlay } from './AdmissionCallToActionOverlay/AdmissionCallToActionOverlay';
import type { AdmissionCallToActionStatus } from './AdmissionCallToActionStatus';

export interface AdmissionCallToActionProps {
    mainLabel: string;
    helperLabel: string;
    translations: StudyAdmissionCallToActionTranslations;
    campusCards: CampusCardBlockViewModel[];
    campusApplicationUrl: string;
    status: AdmissionCallToActionStatus;
    choices?: ApplicationChoiceDto[];
    theme?: Theme;
    link?: Pick<LinkViewModel, 'href' | 'label'>;
}

export const AdmissionCallToAction: React.FC<AdmissionCallToActionProps> = props => {
    const contextualTheme = useTheme();
    const {
        mainLabel,
        helperLabel,
        choices,
        campusApplicationUrl,
        campusCards,
        status,
        translations,
        link,
        theme = contextualTheme,
    } = props;

    const { withBoxShadow, withRoundedCorners } = theme;

    React.useEffect(() => {
        if (link) {
            const handler = {
                handle: () => {
                    redirect(link.href);
                },
            };

            MessageBroker.subscribe(handler, NavigationApplyClick.id);

            return () => {
                MessageBroker.unsubscribe(handler, NavigationApplyClick.id);
            };
        }
    }, [link]);

    React.useEffect(() => {
        const isNonApplicableLink = !!link && status === 'closed';

        MessageBroker.schedulePublish(
            new AdmissionCallToActionOverlayStatusChanged('nonApplicableLink', isNonApplicableLink),
            0
        );
    }, [status, link]);

    return (
        <div
            className={classNames(
                styles.AdmissionCallToAction,
                withBoxShadow && styles['Variant--shadowed'],
                withRoundedCorners && styles['Variant--rounded']
            )}
        >
            <div className={classNames(styles.Content, styles[`Content--${status}`])}>
                <span className={styles.Label}>{mainLabel}</span>
                <span className={styles.SubLabel}>{helperLabel}</span>
            </div>
            <div>
                {link ? (
                    <Link
                        type={LinkType.Button}
                        buttonTier={ButtonTier.Primary}
                        buttonSize={ButtonSize.XLarge}
                        className={styles.Action}
                        href={link.href}
                        theme={theme}
                    >
                        {link.label ?? translations.similarStudies}
                        <SvgIcon iconName="arrow-right" />
                    </Link>
                ) : (
                    choices && (
                        <AdmissionCallToActionOverlay
                            heading={translations.applyFor}
                            buttonLabel={
                                choices.length > 1
                                    ? translations.applyForStudyMultipleLocations
                                    : translations.applyForStudy
                            }
                            closeLabel={translations.closeOverlay}
                            campusApplicationUrl={campusApplicationUrl}
                            choices={choices}
                            status={status}
                            theme={theme}
                        >
                            <AdmissionCallToActionCardsList
                                campusApplicationUrl={campusApplicationUrl}
                                choices={choices}
                                cards={campusCards}
                                submitButtonLabel={translations.goToBasket}
                                theme={theme}
                                variantAlreadyInBasketAlertMessage={translations.variantAlreadyInBasket}
                            />
                        </AdmissionCallToActionOverlay>
                    )
                )}
            </div>
        </div>
    );
};
